import React, { useState, useEffect } from "react";
import api from "../../../../services/api";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Badge,
  Spinner,
  Card,
  CardBody,
  CardHeader,
} from "reactstrap";

export default ({ isOpen, toggle, clienteId, empresaId, notify, onUpdate }) => {
  const [imoveis, setImoveis] = useState([]);
  const [cultivos, setCultivos] = useState([]);
  const [cultivosDisponiveis, setCultivosDisponiveis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedImovel, setSelectedImovel] = useState(null);
  const [selectedCultivo, setSelectedCultivo] = useState("");

  useEffect(() => {
    if (isOpen && clienteId && empresaId) {
      loadImoveis();
      loadCultivosDisponiveis();
    }
  }, [isOpen, clienteId, empresaId]);

  useEffect(() => {
    if (selectedImovel) {
      loadCultivosDoImovel(selectedImovel.id);
    }
  }, [selectedImovel]);

  async function loadImoveis() {
    setLoading(true);
    try {
      const response = await api.get(`/common/empresas/${empresaId}/clientes/${clienteId}/imoveis`);
      const imoveisData = response.data.data || [];
      setImoveis(imoveisData);

      if (imoveisData.length > 0 && !selectedImovel) {
        setSelectedImovel(imoveisData[0]);
      }
    } catch (error) {
      console.error("Erro ao carregar imóveis:", error);
      notify("danger", "Erro ao carregar imóveis");
    } finally {
      setLoading(false);
    }
  }

  async function loadCultivosDisponiveis() {
    try {
      const response = await api.get(`/common/cultivos`);
      setCultivosDisponiveis(response.data.data || []);
    } catch (error) {
      console.error("Erro ao carregar cultivos disponíveis:", error);
    }
  }

  async function loadCultivosDoImovel(imovelId) {
    setLoading(true);
    try {
      const response = await api.get(
        `/common/empresas/${empresaId}/clientes/${clienteId}/imoveis/${imovelId}/cultivos`
      );
      setCultivos(response.data.data || []);
    } catch (error) {
      console.error("Erro ao carregar cultivos do imóvel:", error);
      notify("danger", "Erro ao carregar cultivos do imóvel");
    } finally {
      setLoading(false);
    }
  }

  const handleAdicionarCultivo = async (e) => {
    e.preventDefault();
    if (!selectedCultivo || !selectedImovel) return;

    setSaving(true);
    try {
      await api.post(
        `/common/empresas/${empresaId}/clientes/${clienteId}/imoveis/${selectedImovel.id}/cultivos`,
        { cultivo_id: selectedCultivo }
      );

      notify("success", "Cultivo adicionado com sucesso");
      setSelectedCultivo("");
      loadCultivosDoImovel(selectedImovel.id);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Erro ao adicionar cultivo:", error);
      if (error.response?.status === 409) {
        notify("warning", "Este cultivo já está associado a esta propriedade");
      } else {
        notify("danger", "Erro ao adicionar cultivo");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRemoverCultivo = async (cultivoId) => {
    if (!selectedImovel) return;

    if (window.confirm("Deseja remover este cultivo da propriedade?")) {
      try {
        await api.delete(
          `/common/empresas/${empresaId}/clientes/${clienteId}/imoveis/${selectedImovel.id}/cultivos/${cultivoId}`
        );

        notify("success", "Cultivo removido com sucesso");
        loadCultivosDoImovel(selectedImovel.id);
        if (onUpdate) onUpdate();
      } catch (error) {
        console.error("Erro ao remover cultivo:", error);
        notify("danger", "Erro ao remover cultivo");
      }
    }
  };

  const cultivosNaoAssociados = cultivosDisponiveis.filter(
    cultivo => !cultivos.some(c => c.id === cultivo.id)
  );

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        Cultivos por Propriedade
      </ModalHeader>
      <ModalBody>
        {imoveis.length === 0 ? (
          <div className="text-center py-4">
            <i className="fas fa-home fa-3x text-muted mb-3" />
            <h5 className="text-muted">Nenhuma propriedade cadastrada</h5>
            <p className="text-muted">
              É necessário cadastrar pelo menos uma propriedade antes de adicionar cultivos.
            </p>
          </div>
        ) : (
          <>
            <Row className="mb-3">
              <Col>
                <Label for="imovel-select">Selecione a Propriedade:</Label>
                <Input
                  type="select"
                  id="imovel-select"
                  value={selectedImovel?.id || ""}
                  onChange={(e) => {
                    const imovel = imoveis.find(i => i.id === parseInt(e.target.value));
                    setSelectedImovel(imovel);
                  }}
                >
                  {imoveis.map((imovel) => (
                    <option key={imovel.id} value={imovel.id}>
                      {imovel.nome}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>

            {selectedImovel && (
              <>
                <Card className="mb-3">
                  <CardHeader>
                    <h6 className="mb-0">Adicionar Novo Cultivo</h6>
                  </CardHeader>
                  <CardBody>
                    <Form onSubmit={handleAdicionarCultivo}>
                      <Row>
                        <Col md="8">
                          <FormGroup>
                            <Input
                              type="select"
                              value={selectedCultivo}
                              onChange={(e) => setSelectedCultivo(e.target.value)}
                              required
                            >
                              <option value="">Selecione um cultivo</option>
                              {cultivosNaoAssociados.map((cultivo) => (
                                <option key={cultivo.id} value={cultivo.id}>
                                  {cultivo.nome}
                                </option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <Button
                            type="submit"
                            color="primary"
                            disabled={saving || !selectedCultivo}
                            block
                          >
                            {saving && <Spinner size="sm" className="mr-1" />}
                            Adicionar
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <h6 className="mb-0">
                      Cultivos da Propriedade: {selectedImovel.nome}
                    </h6>
                  </CardHeader>
                  <CardBody>
                    {loading ? (
                      <div className="text-center py-4">
                        <Spinner color="primary" />
                      </div>
                    ) : cultivos.length === 0 ? (
                      <div className="text-center py-4">
                        <i className="fas fa-seedling fa-2x text-muted mb-2" />
                        <p className="text-muted mb-0">
                          Nenhum cultivo associado a esta propriedade
                        </p>
                      </div>
                    ) : (
                      <Table responsive striped>
                        <thead>
                          <tr>
                            <th>Cultivo</th>
                            <th>Descrição</th>
                            <th>Data de Associação</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cultivos.map((item) => (
                            <tr key={item.id}>
                              <td>
                                <Badge color="success" pill>
                                  {item.nome}
                                </Badge>
                              </td>
                              <td>
                                {item.descricao ? (
                                  <span>{item.descricao.substring(0, 50)}{item.descricao.length > 50 ? '...' : ''}</span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                {item.created_at ? new Date(item.created_at).toLocaleDateString('pt-BR') : '-'}
                              </td>
                              <td>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleRemoverCultivo(item.id)}
                                >
                                  <i className="fas fa-trash" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </CardBody>
                </Card>
              </>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};
